
type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';

interface LogConfig {
  level: LogLevel;
  prefix: string;
  color?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

const LOG_CONFIGS: Record<LogLevel, LogConfig> = {
  ERROR: { level: 'ERROR', prefix: '[ERROR]', color: 'color: #ef4444' },
  WARN: { level: 'WARN', prefix: '[WARN]', color: 'color: #f59e0b' },
  INFO: { level: 'INFO', prefix: '[INFO]', color: 'color: #3b82f6' },
  DEBUG: { level: 'DEBUG', prefix: '[DEBUG]', color: 'color: #6b7280' },
};

class Logger {
  private currentLevel: LogLevel;

  constructor() {
    // Only show ERROR and WARN in production, all levels in development
    this.currentLevel = import.meta.env.PROD ? 'WARN' : 'DEBUG';
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] <= LOG_LEVELS[this.currentLevel];
  }

  private formatMessage(level: LogLevel, context: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    return `${LOG_CONFIGS[level].prefix} [${timestamp}] [${context}] ${message}`;
  }

  error(context: string, message: string, error?: any) {
    if (!this.shouldLog('ERROR')) return;
    
    const formattedMessage = this.formatMessage('ERROR', context, message);
    if (error) {
      console.error(`%c${formattedMessage}`, LOG_CONFIGS.ERROR.color, error);
    } else {
      console.error(`%c${formattedMessage}`, LOG_CONFIGS.ERROR.color);
    }
  }

  warn(context: string, message: string, data?: any) {
    if (!this.shouldLog('WARN')) return;
    
    const formattedMessage = this.formatMessage('WARN', context, message);
    if (data) {
      console.warn(`%c${formattedMessage}`, LOG_CONFIGS.WARN.color, data);
    } else {
      console.warn(`%c${formattedMessage}`, LOG_CONFIGS.WARN.color);
    }
  }

  info(context: string, message: string, data?: any) {
    if (!this.shouldLog('INFO')) return;
    
    const formattedMessage = this.formatMessage('INFO', context, message);
    if (data) {
      console.info(`%c${formattedMessage}`, LOG_CONFIGS.INFO.color, data);
    } else {
      console.info(`%c${formattedMessage}`, LOG_CONFIGS.INFO.color);
    }
  }

  debug(context: string, message: string, data?: any) {
    if (!this.shouldLog('DEBUG')) return;
    
    const formattedMessage = this.formatMessage('DEBUG', context, message);
    if (data) {
      console.log(`%c${formattedMessage}`, LOG_CONFIGS.DEBUG.color, data);
    } else {
      console.log(`%c${formattedMessage}`, LOG_CONFIGS.DEBUG.color);
    }
  }
}

export const logger = new Logger();
